import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { registerApi, ecpVerifyApi, acceptConsentApi } from "../api";
import { useAuth } from "../context/AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import EcpButton from "../components/EcpButton";
import { normalizeApiError } from "../lib/apiError";

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== password2) {
      setErr(t("auth.passwords_mismatch"));
      return;
    }
    if (password.length < 6) {
      setErr(t("auth.password_too_short"));
      return;
    }
    setBusy(true);
    try {
      await registerApi({ email: email.trim(), password, name: name.trim() });
      try { await acceptConsentApi("register"); } catch (_e) { /* banner will retry */ }
      await refresh();
      navigate("/dashboard");
    } catch (e2) {
      setErr(normalizeApiError(e2, t("auth.register_error")));
    } finally { setBusy(false); }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card" data-testid="register-card">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Link to="/" className="small text-muted text-decoration-none"
                data-testid="register-back-home" title={t("auth.back_to_home")}>
            <i className="bi bi-arrow-left me-1"></i>{t("auth.back_to_home")}
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="text-center mb-4">
          <Link to="/" className="text-decoration-none" data-testid="register-brand-home"
                title={t("auth.back_to_home")}>
            <div className="d-inline-flex align-items-center justify-content-center mb-2"
                 style={{ width: 56, height: 56, background: "#0b3d91", borderRadius: 14, color: "white", cursor: "pointer" }}>
              <i className="bi bi-person-plus-fill fs-3"></i>
            </div>
            <h3 className="fw-bold mb-1 text-dark">{t("auth.register_title")}</h3>
          </Link>
          <p className="text-muted small mb-0">{t("auth.register_subtitle")}</p>
        </div>

        {err && <div className="alert alert-danger py-2" data-testid="register-error">{err}</div>}

        <form onSubmit={onSubmit}>
          <div className="mb-3">
            <label className="form-label small">{t("common.fullname")}</label>
            <input className="form-control" required name="name" value={name}
                   onChange={(e) => setName(e.target.value)} data-testid="register-name" />
          </div>
          <div className="mb-3">
            <label className="form-label small">{t("common.email")}</label>
            <input type="email" className="form-control" required name="email" value={email}
                   onChange={(e) => setEmail(e.target.value)} data-testid="register-email" />
          </div>
          <div className="mb-3">
            <label className="form-label small">{t("auth.password_min_label")}</label>
            <input type="password" className="form-control" required minLength={6}
                   name="password" value={password}
                   onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
          </div>
          <div className="mb-3">
            <label className="form-label small">{t("auth.password_repeat")}</label>
            <input type="password" className="form-control" required minLength={6}
                   name="password2" value={password2}
                   onChange={(e) => setPassword2(e.target.value)} data-testid="register-password2" />
          </div>
          <div className="form-check mb-3 small">
            <input className="form-check-input" type="checkbox" id="agree-terms" checked={agree}
                   onChange={(e) => setAgree(e.target.checked)} data-testid="register-agree" />
            <label className="form-check-label text-muted" htmlFor="agree-terms">
              {t("auth.register_agree_prefix")}{" "}
              <Link to="/terms" target="_blank">{t("legal.terms_link")}</Link>{" "}
              {t("legal.and")}{" "}
              <Link to="/privacy" target="_blank">{t("legal.privacy_link")}</Link>
              {t("auth.register_agree_suffix")}
            </label>
          </div>
          <button className="btn btn-primary w-100" disabled={busy || !agree} data-testid="register-submit">
            {busy ? t("auth.register_creating") : t("auth.register_button")}
          </button>
        </form>

        <div className="text-center my-3 text-muted small">{t("auth.or")}</div>
        <div style={!agree ? { opacity: 0.55, pointerEvents: "none" } : undefined}
             title={!agree ? t("auth.agree_first") : undefined}>
        <EcpButton
          label={t("auth.ecp_register")}
          testId="register-ecp"
          onSigned={async (challenge_id, cms_b64) => {
            const resp = await ecpVerifyApi(challenge_id, cms_b64);
            try { await acceptConsentApi("ecp"); } catch (_e) { /* banner will retry */ }
            if (resp?.suggest_company && !resp.suggest_company.exists) {
              try {
                sessionStorage.setItem("ecp_suggest_company", JSON.stringify(resp.suggest_company));
              } catch (_e) { /* ignore */ }
            }
            await refresh();
            navigate("/dashboard");
          }}
        />
        </div>
        <div className="small text-muted mt-2 text-center">
          {t("auth.ecp_register_hint")}
        </div>

        <div className="text-center mt-4 small">
          {t("auth.have_account")}{" "}
          <Link to="/login" data-testid="register-link-login">{t("auth.login_button")}</Link>
        </div>
        <div className="text-muted text-center small mt-2">
          {t("auth.register_hr_hint")}
        </div>
      </div>
    </div>
  );
}
